import * as React from 'react'
import {Drawer, Box, Typography, IconButton, Button, TextField, Alert, Tooltip} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import {sectionCardSx, primaryBtnSx, secondaryBtnSx} from '../../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../../styles/foundation/typography'
import {NAVY, SUBTLE, BORDER, WARNING} from '../../../../styles/foundation/colors'
import {GAP} from '../../../../styles/foundation/spacing'
import {int, kg, pct} from '../format'
import KeyValue from '../components/KeyValue'

/** The same threshold the mockup uses to decide what the spread is telling the merchant — one wide-standing-in-for-many-products story above it, one single-product story at or below it. Pure and exported so both the KeyValue tone and the footnote text are driven off ONE number, never two copies of "25". */
export function isWideSpread(spread) {
  return Number(spread) > 25
}

/**
 * The real-product-code guard, verbatim from the task brief: at least 2
 * characters, and not already claimed by a live SKU (case-insensitively —
 * "sn-shirt-navy-m" and "SN-SHIRT-NAVY-M" are the same clash). Pure and
 * exported so this — the one thing standing between a merchant and handing a
 * still-unusable code back to `applyFixCode` — is unit-tested without
 * mounting the drawer.
 */
export function validateNewCode(next, skus) {
  const trimmed = String(next || '').trim()
  const clash = trimmed.length > 0 && (skus || []).some((x) => x.sku.toLowerCase() === trimmed.toLowerCase())
  const ok = trimmed.length >= 2 && !clash
  return {trimmed, clash, ok}
}

const labelSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}
const alertSx = {fontFamily: FONT, fontSize: SIZE['2xs'], py: 0.75, '& .MuiAlert-message': {lineHeight: 1.55}}

/**
 * Fix-code drawer for a `dq: true` SKU — Catalogue Task 7. These codes are
 * genuinely unusable IN THE SOURCE (truncated to nothing, or two products
 * colliding on an identical truncated prefix — see noberoFreeze.js's own
 * header comment) — this drawer's copy says that plainly rather than
 * implying the panel failed to parse something it received cleanly.
 *
 * `sku` is the live store record for the row that opened this drawer,
 * resolved fresh off `skus` by code every render (same as ReviewSkuDrawer);
 * `skus` is the full live list, needed only for the clash check above — a
 * renamed code must not collide with any OTHER code already in the
 * catalogue, dq or not.
 *
 * Keyboard-dismissable and focus-return are handled by MUI's underlying
 * Modal exactly like the sibling drawers; CataloguePage's own
 * `closeFixCodeDrawer` reuses the SAME `focusFallbackRef` fallback as
 * MergeDrawer/UploadDrawer/ReviewSkuDrawer rather than inventing a second
 * mechanism.
 */
export default function FixCodeDrawer({open, sku, skus, onClose, onSave}) {
  const s = sku || {}

  const [code, setCode] = React.useState('')
  const [prevOpen, setPrevOpen] = React.useState(open)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open) setCode('')
  }

  const {trimmed, clash, ok} = validateNewCode(code, skus)
  const wide = isWideSpread(s.spread)

  const reason = trimmed.length === 0
    ? 'Enter the real product code.'
    : trimmed.length < 2
      ? 'Enter a code at least 2 characters long.'
      : clash
        ? 'That code is already in the catalogue.'
        : ''

  const handleSave = () => {
    if (!ok || !s.sku) return
    onSave(s.sku, trimmed)
  }

  return (
    <Drawer anchor='right' open={open} onClose={onClose} sx={{'& .MuiDrawer-paper': {width: {xs: '100%', sm: 560}}}}>
      <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
        <Box sx={{p: 2, borderBottom: `1px solid ${BORDER}`}}>
          <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1}}>
            <Box sx={{minWidth: 0}}>
              <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.md, color: NAVY}}>
                Give this shipment set a real product code
              </Typography>
              <Typography sx={{...labelSx, fontFamily: 'monospace', wordBreak: 'break-all'}}>{s.sku}</Typography>
            </Box>
            <IconButton onClick={onClose} aria-label='Close fix-code drawer' size='small'>
              <CloseIcon fontSize='small' />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{p: 2, overflowY: 'auto', flex: 1, display: 'grid', gap: GAP.md, alignContent: 'start'}}>
          <Alert severity='warning' sx={alertSx}>
            <Box component='span' sx={{fontWeight: WEIGHT.bold}}>{int(s.ships)} shipments</Box> reached Prozo
            carrying <Box component='span' sx={{fontFamily: 'monospace'}}>{s.sku}</Box> as the product code.
            That is not a code we can hold a weight against, so none of these shipments can join a freeze until
            the code is corrected at source.
          </Alert>

          <Box sx={sectionCardSx}>
            <KeyValue items={[
              {label: 'Shipments affected', value: int(s.ships)},
              {label: 'Most probable weight', value: kg(s.mpw)},
              {
                label: 'Weight spread',
                value: wide ? <Box component='span' sx={{color: WARNING}}>{pct(s.spread)}</Box> : pct(s.spread),
              },
            ]} />
            <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, lineHeight: 1.55, mt: 1.25}}>
              {wide
                ? 'A spread this wide is what you would expect when one code is standing in for many different products.'
                : 'The charged weight is consistent, which suggests one product behind the bad code.'}
            </Typography>
          </Box>

          <TextField
            required
            size='small'
            label='Real product code'
            placeholder='For example SN-SHIRT-NAVY-M'
            value={code}
            onChange={(e) => setCode(e.target.value)}
            error={clash}
            helperText={clash ? 'That code is already in the catalogue.' : 'Use the code your own system holds for these shipments.'}
            sx={{fontFamily: FONT, fontSize: SIZE.xs}}
          />

          <Alert severity='info' sx={alertSx}>
            Renaming here fixes the catalogue. The order source also has to send the real code, or the next
            shipment arrives unusable again.
          </Alert>
        </Box>

        <Box sx={{p: 1.5, borderTop: `1px solid ${BORDER}`, display: 'flex', gap: 1, justifyContent: 'flex-end'}}>
          <Button sx={secondaryBtnSx} onClick={onClose}>Cancel</Button>
          <Tooltip title={ok ? '' : reason}>
            <span>
              <Button sx={primaryBtnSx} disabled={!ok} onClick={handleSave}>Save product code</Button>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Drawer>
  )
}
